import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  lnpLookup, type LnpResult,
  deepVerify, deepVerifyList, getVerifyJob, getVerifyResults,
  type VerifyResult, type VerifyJob,
} from "../api/lookup";
import { listGroups, type ContactGroup } from "../api/contacts";
import { getMyPermissions } from "../api/users";

const MAX_TNS = 5000;
const MAX_DEEP = 100; // EDQ accepts 100 per request

// Pull every distinct 10-digit number out of pasted text (handles +1, dashes,
// dots, parens, spaces, commas, one-per-line, CSV columns, spreadsheet tabs —
// whatever the user pastes). Matching on the NANP 3-3-4 structure (rather than
// a loose digit run) keeps each match within a single number so it can't span
// a newline and swallow two numbers into one over-long, discarded string.
function parseNumbers(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const re = /(?:\+?1[\s.\-]?)?\(?(\d{3})\)?[\s.\-]?(\d{3})[\s.\-]?(\d{4})/g;
  for (const m of text.matchAll(re)) {
    const d = m[1] + m[2] + m[3];
    if (!seen.has(d)) {
      seen.add(d);
      out.push(d);
    }
  }
  return out;
}

function fmtTn(tn: string): string {
  return tn.length === 10 ? `(${tn.slice(0, 3)}) ${tn.slice(3, 6)}-${tn.slice(6)}` : tn;
}

function LnpStatusBadge({ status, portable }: { status: string; portable: boolean }) {
  const cls = status.startsWith("Unknown") || status.startsWith("Invalid")
    ? "bg-gray-100 text-gray-600"
    : portable ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700";
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{status}</span>;
}

function VerifyStatusBadge({ r }: { r: VerifyResult }) {
  let cls = "bg-gray-100 text-gray-600";
  let label = r.verify_status || (r.error ? "Failed" : "—");
  if (!r.success) cls = "bg-red-100 text-red-700";
  else if (r.verify_status === "Verified") cls = "bg-green-100 text-green-700";
  else if (["Dead", "Invalid", "Absent"].includes(r.verify_status || "")) cls = "bg-red-100 text-red-700";
  else cls = "bg-amber-100 text-amber-700";
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}

function downloadCsv(filename: string, cols: string[], rows: Record<string, unknown>[]) {
  const csv = [cols.join(",")]
    .concat(rows.map((r) => cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(",")))
    .join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function Stat({ label, v, tone }: { label: string; v: number; tone?: "good" | "warn" | "bad" }) {
  const color = tone === "good" ? "text-green-700" : tone === "warn" ? "text-amber-700"
    : tone === "bad" ? "text-red-700" : "text-gray-800";
  return (
    <div className="bg-white border border-gray-200 rounded p-3">
      <div className={`text-2xl font-bold ${color}`}>{v.toLocaleString()}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

type Mode = "lnp" | "deep";

export function NumberLookupPage() {
  const { data: me } = useQuery({ queryKey: ["me-permissions"], queryFn: getMyPermissions });
  const allowed = (me?.is_admin ?? false) || (me?.permissions ?? []).includes("lnp_lookup");

  const [mode, setMode] = useState<Mode>("lnp");
  const [text, setText] = useState("");
  const parsed = useMemo(() => parseNumbers(text), [text]);

  if (me && !allowed) {
    return (
      <div>
        <h1 className="text-2xl font-bold mb-4">Number Lookup</h1>
        <p className="text-red-600">You don't have access to Number Lookup. Ask an admin to enable it for your account.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Number Lookup</h1>
        <p className="text-sm text-gray-500 mt-1">
          Check phone numbers for validity, line type, carrier and portability. <b>LNP</b> is
          instant and free. <b>Deep Verify</b> adds live network status, mobile/landline,
          port date, current &amp; original carrier and a disposable flag — and saves the
          results so you keep them.
        </p>
      </div>

      <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
        <button
          onClick={() => setMode("lnp")}
          className={`px-4 py-1.5 ${mode === "lnp" ? "bg-brand text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
        >LNP (fast)</button>
        <button
          onClick={() => setMode("deep")}
          className={`px-4 py-1.5 border-l border-gray-300 ${mode === "deep" ? "bg-brand text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
        >Deep Verify</button>
      </div>

      {mode === "lnp"
        ? <LnpPanel text={text} setText={setText} parsed={parsed} />
        : <DeepPanel text={text} setText={setText} parsed={parsed} />}

      {mode === "deep" && <ListVerifyPanel />}
    </div>
  );
}

// ---------------- LNP (fast) ----------------
function LnpPanel({ text, setText, parsed }: { text: string; setText: (s: string) => void; parsed: string[] }) {
  const tooMany = parsed.length > MAX_TNS;
  const lookup = useMutation<LnpResult[], Error, string[]>({ mutationFn: (tns) => lnpLookup(tns) });
  const rows = lookup.data ?? [];

  const summary = useMemo(() => {
    const s = { total: rows.length, portable: 0, wireless: 0, landline: 0, unknown: 0 };
    for (const r of rows) {
      if (r.portable) s.portable++;
      if (r.line_type === "Wireless") s.wireless++;
      else if (r.line_type === "Landline") s.landline++;
      if (r.status.startsWith("Unknown")) s.unknown++;
    }
    return s;
  }, [rows]);

  return (
    <div className="space-y-4">
      <PasteBox text={text} setText={setText} parsed={parsed} tooMany={tooMany} max={MAX_TNS}
        busy={lookup.isPending} label={`Check ${parsed.length || ""} number${parsed.length === 1 ? "" : "s"}`}
        onRun={() => lookup.mutate(parsed)} error={lookup.isError ? lookup.error.message : null} />

      {rows.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Stat label="Checked" v={summary.total} />
            <Stat label="Portable / active" v={summary.portable} tone="good" />
            <Stat label="Wireless" v={summary.wireless} />
            <Stat label="Landline" v={summary.landline} />
            <Stat label="Unknown / invalid" v={summary.unknown} tone={summary.unknown ? "warn" : undefined} />
          </div>
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <div className="px-4 py-2 flex items-center justify-between border-b border-gray-100">
              <span className="text-xs text-gray-500">{rows.length.toLocaleString()} result(s)</span>
              <button onClick={() => downloadCsv("lnp-results.csv",
                ["tn", "status", "portable", "line_type", "carrier", "spid", "rate_center", "city", "state", "lata", "min_porting_interval"],
                rows as unknown as Record<string, unknown>[])}
                className="text-sm text-brand hover:underline">Export CSV</button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-500"><tr>
                <th className="px-3 py-2">Number</th><th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Line type</th><th className="px-3 py-2">Carrier</th>
                <th className="px-3 py-2">State</th><th className="px-3 py-2">Rate center</th>
                <th className="px-3 py-2">LATA</th><th className="px-3 py-2">Port in (days)</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.tn}>
                    <td className="px-3 py-2 font-medium">{fmtTn(r.tn)}</td>
                    <td className="px-3 py-2"><LnpStatusBadge status={r.status} portable={r.portable} /></td>
                    <td className="px-3 py-2">{r.line_type || "—"}</td>
                    <td className="px-3 py-2">{r.carrier || "—"}</td>
                    <td className="px-3 py-2">{r.state || "—"}</td>
                    <td className="px-3 py-2">{r.rate_center || "—"}</td>
                    <td className="px-3 py-2">{r.lata || "—"}</td>
                    <td className="px-3 py-2">{r.min_porting_interval || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------- Deep Verify (paste) ----------------
function DeepPanel({ text, setText, parsed }: { text: string; setText: (s: string) => void; parsed: string[] }) {
  const tooMany = parsed.length > MAX_DEEP;
  const [jobId, setJobId] = useState<number | null>(null);
  const start = useMutation<{ job_id: number }, Error, string[]>({
    mutationFn: (n) => deepVerify(n),
    onSuccess: (d) => setJobId(d.job_id),
  });
  const { data: job } = useQuery({
    queryKey: ["verify-job", jobId],
    queryFn: () => getVerifyJob(jobId as number),
    enabled: jobId != null,
    refetchInterval: (q) => (q.state.data?.status === "complete" || q.state.data?.status === "failed" ? false : 4000),
  });
  const rows = job?.results ?? [];

  return (
    <div className="space-y-4">
      <PasteBox text={text} setText={setText} parsed={parsed} tooMany={tooMany} max={MAX_DEEP}
        busy={start.isPending || (job != null && job.status !== "complete" && job.status !== "failed")}
        label={`Deep verify ${parsed.length || ""} number${parsed.length === 1 ? "" : "s"}`}
        onRun={() => { setJobId(null); start.mutate(parsed); }}
        error={start.isError ? start.error.message : null} />

      {job && job.status !== "complete" && (
        <JobProgress job={job} note="Deep verify retries the carrier network behind the scenes, so this can take a few minutes." />
      )}

      {rows.length > 0 && <DeepResultsTable rows={rows} filename="deep-verify-results.csv" />}
    </div>
  );
}

// ---------------- Deep Verify (whole saved list) ----------------
function ListVerifyPanel() {
  const { data: groups } = useQuery({ queryKey: ["contact-groups", "active"], queryFn: () => listGroups("active") });
  const [groupId, setGroupId] = useState<number | null>(null);
  const [jobId, setJobId] = useState<number | null>(null);
  const [preview, setPreview] = useState<VerifyResult[]>([]);

  const start = useMutation<{ job_id: number }, Error, number>({
    mutationFn: (g) => deepVerifyList(g),
    onSuccess: (d) => { setJobId(d.job_id); setPreview([]); },
  });
  const { data: job } = useQuery({
    queryKey: ["verify-list-job", jobId],
    queryFn: () => getVerifyJob(jobId as number),
    enabled: jobId != null,
    refetchInterval: (q) => (q.state.data?.status === "complete" || q.state.data?.status === "failed" ? false : 5000),
  });

  // When the list job completes, load a preview page of stored results.
  useEffect(() => {
    if (job?.status === "complete" && job.contact_group_id) {
      getVerifyResults({ contact_group_id: job.contact_group_id, per_page: 200 }).then(setPreview).catch(() => {});
    }
  }, [job?.status, job?.contact_group_id]);

  async function exportAll() {
    if (!job?.contact_group_id) return;
    const all = await getVerifyResults({ contact_group_id: job.contact_group_id, per_page: 10000 });
    downloadCsv("list-verify-results.csv",
      ["phone", "phone_number", "verify_status", "phone_type", "is_ported", "ported_date",
        "operator_name", "original_operator", "network_status", "disposable", "country", "error"],
      all as unknown as Record<string, unknown>[]);
  }

  const busy = start.isPending || (job != null && job.status !== "complete" && job.status !== "failed");

  return (
    <div className="bg-white rounded-lg shadow p-4 space-y-3 border-t-4 border-brand/30">
      <div>
        <h2 className="font-semibold text-gray-800">Verify a whole saved list</h2>
        <p className="text-xs text-gray-500">
          Pick one of your contact lists and we'll deep-verify every number in it, 100 at a
          time, in the background — retrying the carrier network until each number resolves.
          Results are saved so you can export them when it's done.
        </p>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <select
          className="border border-gray-300 rounded px-3 py-2 text-sm min-w-[16rem]"
          value={groupId ?? ""}
          onChange={(e) => setGroupId(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Select a list…</option>
          {(groups ?? []).map((g: ContactGroup) => (
            <option key={g.contact_group_id} value={g.contact_group_id}>
              {g.name} ({g.contact_count.toLocaleString()})
            </option>
          ))}
        </select>
        <button
          disabled={groupId == null || busy}
          onClick={() => { setJobId(null); start.mutate(groupId as number); }}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm disabled:opacity-50"
        >{busy ? "Verifying…" : "Start verification"}</button>
        {job?.status === "complete" && (
          <button onClick={exportAll} className="text-sm text-brand hover:underline">Export all results CSV</button>
        )}
      </div>
      {start.isError && <p className="text-sm text-red-600">{start.error.message}</p>}

      {job && job.status !== "complete" && (
        <JobProgress job={job} note="You can leave this page — verification keeps running in the background." />
      )}
      {job?.status === "complete" && (
        <p className="text-sm text-green-700">
          Done — {job.verified.toLocaleString()} verified, {job.failed.toLocaleString()} unresolved of {job.total.toLocaleString()}.
        </p>
      )}
      {preview.length > 0 && <DeepResultsTable rows={preview} filename="list-verify-results.csv" hideExport
        caption={`Showing first ${preview.length.toLocaleString()} — use "Export all results CSV" for the full set.`} />}
    </div>
  );
}

// ---------------- shared bits ----------------
function PasteBox(props: {
  text: string; setText: (s: string) => void; parsed: string[];
  tooMany: boolean; max: number; busy: boolean; label: string;
  onRun: () => void; error: string | null;
}) {
  const { text, setText, parsed, tooMany, max, busy, label, onRun, error } = props;
  return (
    <div className="bg-white rounded-lg shadow p-4 space-y-3">
      <textarea
        className="w-full h-40 border border-gray-300 rounded p-3 text-sm font-mono"
        placeholder={"7635019376\n(202) 935-5047\n+1 212 555 0123 ..."}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="flex items-center gap-3 flex-wrap">
        <button
          disabled={parsed.length === 0 || tooMany || busy}
          onClick={onRun}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm disabled:opacity-50"
        >{busy ? "Working…" : label}</button>
        {text && <button onClick={() => setText("")} className="text-sm text-gray-500 hover:underline">Clear</button>}
        <span className="text-xs text-gray-500">
          {parsed.length.toLocaleString()} unique number{parsed.length === 1 ? "" : "s"} detected
          {tooMany && <span className="text-red-600"> — max {max.toLocaleString()} per check, trim the list</span>}
        </span>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

function JobProgress({ job, note }: { job: VerifyJob; note?: string }) {
  return (
    <div className="space-y-1">
      <div className="h-2 w-full bg-gray-200 rounded overflow-hidden">
        <div className="h-full bg-brand transition-all" style={{ width: `${job.pct}%` }} />
      </div>
      <div className="text-xs text-gray-500">
        {job.status === "pending" ? "Queued…" : `Verifying ${job.done.toLocaleString()} / ${job.total.toLocaleString()} (${job.pct}%) — ${job.verified.toLocaleString()} ok, ${job.failed.toLocaleString()} unresolved`}
      </div>
      {note && <div className="text-xs text-gray-400">{note}</div>}
    </div>
  );
}

function DeepResultsTable({ rows, filename, hideExport, caption }:
  { rows: VerifyResult[]; filename: string; hideExport?: boolean; caption?: string }) {
  return (
    <div className="bg-white rounded-lg shadow overflow-x-auto">
      <div className="px-4 py-2 flex items-center justify-between border-b border-gray-100">
        <span className="text-xs text-gray-500">{caption ?? `${rows.length.toLocaleString()} result(s)`}</span>
        {!hideExport && (
          <button onClick={() => downloadCsv(filename,
            ["phone", "phone_number", "verify_status", "phone_type", "is_ported", "ported_date",
              "operator_name", "original_operator", "network_status", "disposable", "country", "error"],
            rows as unknown as Record<string, unknown>[])}
            className="text-sm text-brand hover:underline">Export CSV</button>
        )}
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-gray-500"><tr>
          <th className="px-3 py-2">Number</th><th className="px-3 py-2">Status</th>
          <th className="px-3 py-2">Line type</th><th className="px-3 py-2">Carrier</th>
          <th className="px-3 py-2">Original carrier</th><th className="px-3 py-2">Ported</th>
          <th className="px-3 py-2">Network</th><th className="px-3 py-2">Disposable</th>
        </tr></thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => (
            <tr key={r.phone}>
              <td className="px-3 py-2 font-medium">{fmtTn(r.phone)}</td>
              <td className="px-3 py-2"><VerifyStatusBadge r={r} />{r.error && <span className="ml-1 text-[10px] text-gray-400">{r.error}</span>}</td>
              <td className="px-3 py-2">{r.phone_type || "—"}</td>
              <td className="px-3 py-2">{r.operator_name || "—"}</td>
              <td className="px-3 py-2">{r.original_operator || "—"}</td>
              <td className="px-3 py-2">{r.is_ported === "Yes" ? `Yes${r.ported_date && r.ported_date !== "Not applicable" ? ` (${r.ported_date.split(" ")[0]})` : ""}` : (r.is_ported || "—")}</td>
              <td className="px-3 py-2">{r.network_status || "—"}</td>
              <td className="px-3 py-2">{r.disposable || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
